// LIBRERIE/IMPORTS
import React, { useState } from 'react';

// import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';


// CONFIGURAZIONI
import appConfig, { AGENCY_ID } from '../../config/appConfig';

// HOOKS
import { useGetAgencyDetails } from '../../hooks/useGetAgencyDetails';
import { WidgetMail } from '../Widgets/WidgetMail/WidgetMail';

// COMPONENTI
import SubHeaderDefault from '../Header/subHeader/SubHeaderDefault';
import LoadingIndicator from '../Loaders/LoadingIndicator';
import NotFound from '../Error/NotFound';
import WidgetContacts from '../Widgets/WidgetContacts/WidgetContacts';
import WidgetPartners from '../Widgets/WidgetPartners/WidgetPartners';

interface FAQItem {
    id: string;
    question: string;
    answer: string;
}

const faqsData: FAQItem[] = [
    {
        id: 'faq1',
        question: 'Devo controllare qualcosa prima di vendere casa?',
        answer: 'Assolutamente sì. Prima di mettere in vendita un immobile è fondamentale fare una serie di controlli tecnici e documentali. Senza queste verifiche, potresti andare incontro a ritardi, spese impreviste o addirittura all’annullamento della compravendita.'
    },
    {
        id: 'faq2',
        question: 'Quali sono i controlli essenziali da fare prima di vendere casa?',
        answer: `Ecco i tre più importanti:
             <ol class="pl-4 mt-2">
                <li class="mb-2"><strong>Conformità urbanistica:</strong> Verifica che l’immobile sia stato costruito secondo i permessi e le normative edilizie. Anche piccole difformità possono compromettere la vendita.</li>
                <li class="mb-2"><strong>Conformità catastale:</strong> La planimetria registrata al catasto deve corrispondere allo stato reale dell’immobile. In caso contrario, l’atto notarile potrebbe non essere valido.</li>
                <li class="mb-2"><strong>Agibilità dell’immobile:</strong> L’agibilità attesta che la casa è sicura, salubre e abitabile. In sua assenza, l’acquirente potrebbe avere problemi a ottenere un mutuo.</li>
             </ol>`
    },
    {
        id: 'faq3',
        question: 'Cosa succede se uno di questi documenti manca o non è in regola?',
        answer: 'La trattativa può rallentare o bloccarsi. In alcuni casi, il notaio può rifiutarsi di rogitare. Nei casi peggiori, l’acquirente potrebbe chiedere il rimborso, anche dopo l’acquisto.'
    },
    {
        id: 'faq4',
        question: 'Posso vendere anche se ci sono piccole difformità?',
        answer: 'Dipende dalla situazione specifica. Alcune difformità possono essere sanate, ma è importante intervenire prima di firmare proposte o preliminari. Il consiglio è di far analizzare l’immobile da un professionista qualificato.'
    },
    {
        id: 'faq5',
        question: 'Come posso sapere se la mia casa è pronta per la vendita?',
        answer: 'Chiara Crott offre un servizio di analisi preliminare gratuita, durante la quale vengono verificati tutti i documenti necessari. È il modo migliore per vendere casa in modo sicuro e senza sorprese.'
    },
    {
        id: 'faq6',
        question: 'Chi compra la mia casa può avere problemi con il mutuo se qualcosa non è in regola?',
        answer: 'Assolutamente sì. Un acquirente potrebbe non ottenere il mutuo se l’immobile non è conforme o privo dell’agibilità. Questo può bloccare la vendita, anche all’ultimo momento. Verificare in anticipo è fondamentale.'
    },
    {
        id: 'faq7',
        question: 'Come funziona la caparra nella vendita di un immobile?',
        answer: 'La caparra confirmatoria è una somma versata dall’acquirente al momento della proposta o del preliminare. Serve come impegno: se l\'acquirente si ritira, la perde. Se il venditore cambia idea, dovrà restituirla con importo raddoppiato. È importante gestirla con chiarezza e attraverso un intermediario esperto.'
    },
    {
        id: 'faq8',
        question: 'Cosa fa il notaio nella compravendita?',
        answer: 'Il notaio redige e registra l’atto di compravendita, verificando la legittimità della transazione. Tuttavia, non è sua responsabilità controllare difformità edilizie o urbanistiche: è compito del venditore fornirgli la documentazione corretta e aggiornata.'
    },
    {
        id: 'faq9',
        question: 'Quanto tempo ci vuole per vendere casa?',
        answer: 'Dipende da diversi fattori: zona, prezzo, stato dell\'immobile e strategia di vendita. Una casa ben posizionata e con documentazione pronta può vendersi in circa 2-3 mesi. Il mio obiettivo in ogni trattativa è quello di ridurre i tempi medi di vendita, grazie a valutazioni realistiche, promozione professionale e selezione degli acquirenti.'
    },
];

const SellHome: React.FC = () => {
    const { t } = useTranslation();
    const { agency, loading: agencyLoading, error: agencyError } = useGetAgencyDetails(AGENCY_ID);
    const [accordionActiveIndex, setAccordionActiveIndex] = useState<number | null>(null);

    const handleAccordionToggle = (index: number) => {
        setAccordionActiveIndex(prevIndex => (prevIndex === index ? null : index));
    };
    
    if (agencyLoading) {
        return <LoadingIndicator />;
    }

    if (agencyError) {
        return <NotFound message={t('validation.loadingError') || "Errore durante il caricamento della pagina."} />;
    }

    return (
        <>
            <Helmet>
                <title>Vendere Casa: Strategia e Sicurezza | {agency?.agencyName ?? 'Chiara Crott Immobiliare'}</title>
                <meta name="description" content="Scopri come vendere casa senza stress con il supporto di un professionista. Dalla preparazione dei documenti alla firma dal notaio, un percorso guidato e sicuro." />
            </Helmet>

            <SubHeaderDefault title="Vendere Casa" isSeo={false} />

            <section className="space-ptb">
                <div className="container">
                    <div className="row">
                        <div className="col-lg-6 mb-4">
                            {/* --- SEZIONE INTRODUTTIVA --- */}
                            <div className="section-title">
                                <h2 className="title">Vuoi vendere casa?</h2>
                                <p className="lead text-dark mt-3">
                                    Il momento perfetto non esiste, ma la strategia giusta sì!
                                </p>
                            </div>
                            <p>
                                Vendere casa non è solo una questione di tempismo: è una questione di preparazione, consapevolezza e competenza. Ogni immobile ha una storia da raccontare, ma solo chi conosce davvero il mercato locale può valorizzarla al meglio.
                            </p>
                            <p>
                                Con il mio supporto, vendere casa diventa un percorso guidato e sicuro. Nessuna improvvisazione: ogni dettaglio, dai documenti iniziali alla firma dal notaio, viene curato con attenzione, trasparenza e strategia.
                            </p>
                            <p>
                                In un mercato in continua evoluzione, la differenza non la fa chi ha fretta, ma chi sa pianificare ogni fase in modo professionale.
                            </p>
                            <p className="text-primary mt-4">Il mio obiettivo?</p>
                            <p>
                                Aiutarti a vendere bene e senza stress, evitando errori comuni e valorizzando al massimo il tuo immobile e la tua esperienza.
                            </p>
                        </div>
                        <div className="col-lg-6 mb-4 mb-lg-0">
                            <WidgetMail/>
                        </div>
                        
                        <div id="faq-section" className="col-12">
                            {/* --- SEZIONE FAQ --- */}
                            <div className="section-title">
                                <h2 className="title">FAQ: LE DOMANDE FREQUENTI</h2>
                                <p className="lead">Hai dubbi su come iniziare? Consulta le nostre FAQ per scoprire quali sono i primi passi fondamentali per vendere casa in sicurezza.</p>
                            </div>

                            <div id="accordion" className="accordion">
                                {faqsData.map((faq, index) => (
                                    <div className="card" key={index}>
                                        <div className={`card-header ${accordionActiveIndex === index ? "active" : ""}`} id={`heading-${faq.id}`}>
                                            <h6 className="mb-0">
                                                <a
                                                    href={`#collapse-${faq.id}`}
                                                    className={accordionActiveIndex === index ? "" : "collapsed"}
                                                    data-toggle="collapse"
                                                    data-target={`#collapse-${faq.id}`}
                                                    aria-expanded={accordionActiveIndex === index}
                                                    aria-controls={`collapse-${faq.id}`}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        handleAccordionToggle(index);
                                                    }}
                                                >
                                                    {faq.question}
                                                </a>
                                            </h6>
                                        </div>
                                        <div
                                            id={`collapse-${faq.id}`}
                                            className="collapse"
                                            aria-labelledby={`heading-${faq.id}`}
                                            data-parent="#accordion"
                                        >
                                            <div className="card-body"
                                                dangerouslySetInnerHTML={{ __html: faq.answer }}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* --- SIDEBAR CON CONTATTI --- */}
                        <div className="col-lg-6 mt-4">
                            <WidgetContacts
                                agencyDataAddress={agency?.address_details?.address ?? ''}
                                agencyDataCity={agency?.address_details?.city_name ?? ''}
                                agencyDataComuneCapoluogo={agency?.address_details?.city_name === agency?.address_details?.province_name}
                                agencyDataProvinciaSigla={agency?.address_details?.sigla ?? ''}
                                agencyDataEmail={''}
                                agencyDataEmailShow={appConfig.ui.agencyWidget.showEmail}
                                agencyDataPhone1={agency?.phone1 ?? ''}
                                agencyDataPhone2={agency?.phone2 ?? ''}
                                agencyDataInternationalPrefix={'+39'}
                                agencyDataMobile={agency?.mobile ?? ''}
                                agencyDataMobile2={agency?.mobile2 ?? ''}
                                agencyDataFax={agency?.fax ?? ''}
                                agencySocialShow={appConfig.ui.agencyWidget.showSocialLinks}
                                agencyDataWhatsapp={appConfig.socialLinks.whatsapp}
                                agencyDataFacebook={appConfig.socialLinks.facebook}
                                agencyDataInstagram={appConfig.socialLinks.instagram}
                                agencyDataTwitter={appConfig.socialLinks.twitter}
                                agencyDataLinkedin={appConfig.socialLinks.linkedin}
                                agencyDataPinterest={appConfig.socialLinks.pinterest}
                            />
                        </div>
                        <div className="col-lg-6 mt-4">
                            <WidgetPartners
                                    partners={[
                                        {
                                            url: agency?.id ? `http://www.casain24ore.it/fromsito.php?agReg=${agency.id}` : 'http://www.casain24ore.it',
                                            data: { imgSrc: '/assets/images/c24.png', imgAlt: t('widgets.partners.c24NetworkAlt') || 'Network Immobiliare' }
                                        }
                                    ]}
                                />
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
};

export default SellHome;